<template>
  <PageWrapper>
    <div v-if="kind">
      <MixesHeader
        :title="category.title"
        :description="t(`${kind}_description`)"
        :all-steps="allSteps"
      />
      <div class="overflow-hidden lg:pb-5">
        <div class="container">
          <div class="row">
            <!-- List -->
            <div class="col-12 lg:col-8 xl:col-9 xl:pr-8">
              <MixesStep
                v-for="(step, index) in allSteps"
                :key="step.categoryId"
                :kind="kind"
                :current-step="step"
                :current-step-index="index + 1"
                :all-steps="allSteps"
                :all-ingredients="allIngredients"
                :all-categories="allCategories"
              />
            </div>
            <MixesTube
              :key="tubeKey"
              :kind="kind"
            />
          </div>
        </div>
      </div>
    </div>
    <BaseLoading
      v-else
      :style="{ minHeight: '40vh' }"
    />
    <BaseModal
      modal-id="modal-switch-mix-kind"
      :title="t(`modal_switch_mix_kind_title`)"
    >
      <ModalSwitchMixKind />
    </BaseModal>
    <BaseModal modal-id="mixes-detail-modal">
      <ModalProductDetail />
    </BaseModal>
    <BaseModal modal-id="mix-added-to-cart-modal">
      <ModalAddedToCart />
    </BaseModal>
    <BaseModal
      :title="t('title')"
      modal-id="modal-watchdog"
    >
      <ModalWatchdog />
    </BaseModal>
  </PageWrapper>
  <MixesMobileBottomBar
    :key="tubeKey"
    :kind="kind"
  />
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import BaseLoading from '@/components/general/BaseLoading.vue'
import BaseModal from '@/components/general/BaseModal.vue'
import PageWrapper from '@/components/general/BasePageWrapper.vue'
import MixesHeader from '@/components/mixes/MixesHeader.vue'
import MixesMobileBottomBar from '@/components/mixes/MixesMobileBottomBar.vue'
import MixesStep from '@/components/mixes/MixesStep.vue'
import MixesTube from '@/components/mixes/MixesTube.vue'
import ModalAddedToCart from '@/components/modals/ModalMixAddedToCart.vue'
import ModalProductDetail from '@/components/modals/ModalProductDetail.vue'
import ModalSwitchMixKind from '@/components/modals/ModalSwitchMixKind.vue'
import ModalWatchdog from '@/components/modals/ModalWatchdog.vue'
import useHead from '@/mixins/headManager'
import { MixService } from '@/services/mixService'
import { useCategoriesStore } from '@/stores/CategoriesStore'
import { useMixStore } from '@/stores/MixStore'
import { useModalStore } from '@/stores/ModalStore'
import { useNavbarStore } from '@/stores/NavbarStore'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const permalink = ref(route.params.permalink)
const kind = ref()
const category = ref()
const allIngredients = ref([])
const allCategories = ref([])
const allSteps = ref([])
const tubeKey = ref(0)
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const url = (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.path

const { setNavbar } = useNavbarStore()
const { closeModal, openModal } = useModalStore()

const { getMixCategoriesByPermalink } = useCategoriesStore()

const mixStore = useMixStore()

useHead({
  link: [
    {
      rel: 'canonical',
      href: url,
    },
  ],
})

const forceRerender = () => {
  tubeKey.value += 1
}

const initPage = async () => {
  setNavbar({ waveColor: 'text-primary-100' })
  const { data } = await ApiServiceV1.get(`mix_categories/${permalink.value}`)
  category.value = data.category
  kind.value = data.category.systemName
  allCategories.value = data.categories
  allSteps.value = data.steps
  await loadIngredients(allCategories.value)
  loadEditedMix()
}

const loadEditedMix = () => {
  if (!mixStore.getEditedMix) {
    return
  }
  mixStore.resetMix(mixStore.getEditedMix.kind)
  const mixService = new MixService(mixStore.getEditedMix.kind)
  mixStore.getActualMix.quantity = mixStore.getEditedMix.quantity
  for (const item of mixStore.getEditedMix.items) {
    const ingredient = allIngredients.value.find((i) => i.id === item.productID)
    if (ingredient && item.state === 'available') {
      mixService.addIngredient({ ingredient, quantity: item.quantity })
    }
  }
}

const loadIngredients = async (categories) => {
  const ids = categories.map((c) => c.id).join(',')
  const { data } = await ApiServiceV1.get(`mix_ingredients?kind=${category.value.systemName}&categories=${ids}`)
  allIngredients.value = data.ingredients
}

initPage()

onBeforeRouteUpdate((to, from, next) => {
  // Only when from and to route are mixesPath.
  if (from.name === 'mixesPath' && to.name === 'mixesPath') {
    let allowSwitch = false
    const currentPermalink = from.params.permalink
    const nextPermalink = to.params.permalink
    if (currentPermalink !== nextPermalink) {
      mixStore.cancelEditMix(mixStore.getEditedMix)
      const mix = mixStore.getMix(getMixCategoriesByPermalink(from.params.permalink).kind)
      if (mix?.isEmpty()) {
        allowSwitch = true
      } else if (to.query.force === 'true') {
        // Force param means its force change confirmed by modal.
        router.replace({ query: {} })
        allowSwitch = true
      } else {
        openModal({
          id: 'modal-switch-mix-kind',
          params: {
            permalink: nextPermalink,
          },
        })
      }
      if (allowSwitch) {
        closeModal()
        permalink.value = nextPermalink // Changes localizedKind to "next" route kind
        initPage() // Reloads data
        forceRerender()
        next()
      }
    }
  }
})
</script>

<i18n>
  {
    "cs": {
    "biscuits_description": "Míchej sušenky",
    "coko_description": "Míchej čokoládu",
    "crunchy_fruits_description": "Namixujte si podle chuti plný tubus mrazem sušeného ovoce ",
    "dry_fruits_description": "Namixujte si podle chuti plný tubus oříšků, ovoce a čokolády",
    "fruit_bags_description": "Namixujte si podle chuti Mixit pytlíky do kapsy.",
    "lyophilized_fruits_description": "Lyofilizované ovoce je sušené mrazem, proto tak báječně křupe a chutná skoro jako čerstvé.",
    "mixit_bars_description": "Namixujte si podle chuti plnou krabičku zdravých Mixitek.",
    "modal_switch_mix_kind_title": "Chcete pokračovat?",
    "musli_description": "Jaké si to namícháte, takové to müsli máte. Dobrou chuť!",
    "porridge_description": "Vaše kaše, naše radost! Namixujte si kaši přesně podle chuti.",
    "title": "Chcete si počkat?"
},
    "sk": {
    "biscuits_description": "Miešaj sušienky",
    "coko_description": "Miešaj čokoládu",
    "crunchy_fruits_description": "Namixujte si podľa chuti plný tubus mrazom sušeného ovocia ",
    "dry_fruits_description": "Namixujte si podľa chuti plný tubus orieškov, ovocia a čokolády",
    "fruit_bags_description": "Namixujte si podľa chuti Mixit vrecúška do vačku.",
    "lyophilized_fruits_description": "Lyofilizované ovocie je sušené mrazom, preto tak krásne chrumká a chutí takmer ako čerstvé.",
    "mixit_bars_description": "Namixujte si podľa chuti plnú krabičku zdravých Mixitiek.",
    "modal_switch_mix_kind_title": "Chcete pokračovať?",
    "musli_description": "Aké si to namiešate, také to müsli máte. Dobrú chuť!",
    "porridge_description": "Vaša kaša, naša radosť! Namixujte si kašu presne podľa chuti.",
    "title": "Chcete si počkať?"
},
    "pl": {
    "biscuits_description": "Mixuj ciasteczka",
    "coko_description": "Mixuj czekoladę",
    "crunchy_fruits_description": "Namixuj sobie według gustu tubę pełną liofilizowanych owoców ",
    "dry_fruits_description": "Namixuj sobie według gustu tubę pełną orzeszków, owoców i czekolady",
    "fruit_bags_description": "Namixuj sobie według gustu saszetki do kieszeni.",
    "lyophilized_fruits_description": "Liofilizowane owoce są suszone mrozem, dlatego tak świetnie chrupią i smakują prawie jak świeże.",
    "mixit_bars_description": "Namixuj sobie według gustu box pełen zdrowych Mixitek.",
    "modal_switch_mix_kind_title": "Przejdź do kategorii",
    "musli_description": "Jakie sobie to namieszasz, takie to masz. Smacznego!",
    "porridge_description": "Twoja kasza, nasza radość! Namixuj kaszę według gustu.",
    "title": "Chcesz poczekać?"
},
    "hu": {
    "biscuits_description": "Keverj kekszet",
    "coko_description": "Keverj csokoládét",
    "crunchy_fruits_description": "Keverj ízlésednek megfelelő tele tubus liofilizált gyümölcsöt ",
    "dry_fruits_description": "Keverj ízlésednek megfelelő tubust tele diófélékkel, gyümölccsel és csokoládéval",
    "fruit_bags_description": "Keverj ízlés szerint zsebbevaló Mixit tasakokat.",
    "lyophilized_fruits_description": "A liofilizált gyümölcsök fagyasztva szárítottak, emiatt megőrzik frissességüket és ropogós voltukat.",
    "mixit_bars_description": "Keverj ízlés szerint tele doboz egészséges Mixitkát.",
    "modal_switch_mix_kind_title": "Szeretned folytatni?",
    "musli_description": "Amilyen müzlit keversz, olyan müzlit fogyaszthatsz. Jó étvágyat!",
    "porridge_description": "A te kásád, a mi örömünk. Keverj az ízlésednek megfelelő kását.",
    "title": "Szeretnél várni?"
},
    "de": {
    "biscuits_description": "Mix Kekse",
    "coko_description": "Mixe Schokolade",
    "crunchy_fruits_description": "Eine volle Dose gefriergetrocknete Früchte nach Geschmack mixen.\n\nUm die Knackigkeit der Früchte zu erhalten, lassen Sie die Früchte nach dem Öffnen im Originalbeutel. Verschließen Sie den Beutel fest und legen Sie ihn zurück in die Dose. Wir empfehlen die Verwendung von Verschlussklips, um das Eindringen von Luftfeuchtigkeit zu begrenzen. Lichtgeschützt und trocken lagern. Nach Anbruch unter 20°C aufbewahren.",
    "dry_fruits_description": "Mixe dir eine volle Dose Nüsse, Früchte und Schokolade nach Geschmack",
    "fruit_bags_description": "",
    "lyophilized_fruits_description": "Lyophilisiertes Obst ist gefriergetrocknet, deshalb ist es so wunderbar knusprig und schmeckt fast wie frisch.",
    "mixit_bars_description": "Mix dir eine volle Schachtel gesunder Mixit-Riegel nach deinem Geschmack.",
    "modal_switch_mix_kind_title": "Möchtest du fortfahren?",
    "musli_description": "Dein Mix, dein Moment – Wie du es mixt, so erlebst du es! Guten Appetit!",
    "porridge_description": "Dein Porridge, dein Genuss! Stelle dir dein Porridge ganz nach deinem Geschmack zusammen.",
    "title": "Möchtest du warten?"
},
    "at": {
    "biscuits_description": "Mix Kekse",
    "coko_description": "Mixe Schokolade",
    "crunchy_fruits_description": "Eine volle Dose gefriergetrocknete Früchte nach Geschmack mixen.\n\nUm die Knackigkeit der Früchte zu erhalten, lassen Sie die Früchte nach dem Öffnen im Originalbeutel. Verschließen Sie den Beutel fest und legen Sie ihn zurück in die Dose. Wir empfehlen die Verwendung von Verschlussklips, um das Eindringen von Luftfeuchtigkeit zu begrenzen. Lichtgeschützt und trocken lagern. Nach Anbruch unter 20°C aufbewahren.",
    "dry_fruits_description": "Mixe dir eine volle Dose Nüsse, Früchte und Schokolade nach Geschmack",
    "fruit_bags_description": "",
    "lyophilized_fruits_description": "Lyophilisiertes Obst ist gefriergetrocknet, deshalb ist es so wunderbar knusprig und schmeckt fast wie frisch.",
    "mixit_bars_description": "Mix dir eine volle Schachtel gesunder Mixit-Riegel nach deinem Geschmack.",
    "modal_switch_mix_kind_title": "Möchtest du fortfahren?",
    "musli_description": "Dein Mix, dein Moment – Wie du es mixt, so erlebst du es! Guten Appetit!",
    "porridge_description": "Dein Porridge, dein Genuss! Stelle dir dein Porridge ganz nach deinem Geschmack zusammen.",
    "title": "Möchtest du warten?"
},
    "ro": {
    "biscuits_description": "Amestecă biscuiți",
    "coko_description": "Amestecă ciocolată",
    "crunchy_fruits_description": "Amestecă un tub plin de fructe liofilizate pe gustul tău. ",
    "dry_fruits_description": "Amestecă o cutie plină de nuci, fructe și ciocolată pe gustul tău",
    "fruit_bags_description": "Amestecă punguțe Mixit pe gustul tău.",
    "lyophilized_fruits_description": "Fructele liofilizate sunt congelate și uscate, astfel sunt crocante și au gust aproape ca cele proaspete.",
    "mixit_bars_description": "Amestecă o cutie plină de Mixitka sănătoasă pe gustul tău.",
    "modal_switch_mix_kind_title": "Ești pe cale să treci într-o altă categorie de mix. Vrei să termini amestecul sau să încerci altă categorie?",
    "musli_description": "Cum amesteci, așa musli mănânci. Poftă bună!",
    "porridge_description": "Terciul tău, bucuria noastră! Amestecă terci pe gustul tău.",
    "title": "Dorești să aștepți?"
},
    "fr": {
    "biscuits_description": "Crée tes biscuits",
    "coko_description": "Crée ton chocolat",
    "crunchy_fruits_description": "Composez votre tube rempli de fruits lyophilisés selon vos envies",
    "dry_fruits_description": "Composez votre tube sur mesure de fruits, de noix et de chocolat, selon vos envies",
    "fruit_bags_description": "Composez vos sachets Mixit format poche selon vos envies",
    "lyophilized_fruits_description": "Les fruits lyophilisés sont séchés à froid, ce qui les rend délicieusement croustillants et leur donne presque le goût du fruit frais.",
    "mixit_bars_description": "Composez votre boîte pleine de Mixitka saines, selon vos envies.",
    "modal_switch_mix_kind_title": "Voulez-vous continuer ?",
    "musli_description": "Comme vous le créez, ainsi sera votre muesli. Bon appétit !",
    "porridge_description": "Votre porridge, notre bonheur ! Composez-le exactement selon vos envies.",
    "title": "Voulez-vous patienter ?"
}
  }
</i18n>
